import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Award } from 'lucide-react';
import VideoPlayer from './VideoPlayer';

const AnalysisVideoModal = ({ isOpen, onClose, videoUrl, testName }) => {
  // Check if URL is YouTube
  const isYouTubeUrl = (url) => {
    if (!url) return false;
    try {
      const urlObj = new URL(url);
      return urlObj.hostname.includes('youtube.com') || urlObj.hostname.includes('youtu.be');
    } catch {
      return false;
    }
  };

  // Redirect to YouTube if it's a YouTube URL
  useEffect(() => {
    if (isOpen && videoUrl && isYouTubeUrl(videoUrl)) {
      window.open(videoUrl, '_blank', 'noopener,noreferrer');
      onClose(); // Close modal after opening YouTube
    }
  }, [isOpen, videoUrl, onClose]);

  if (!isOpen) return null;
  
  // If it's YouTube, don't show modal (will redirect)
  if (isYouTubeUrl(videoUrl)) {
    return null;
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
          >
            {/* Modal Content */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-lg">
                    <Award className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                      Analysis by 99 Percentiler
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {testName || 'Test Analysis'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  aria-label="Close"
                >
                  <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                </button>
              </div>

              {/* Video Player */}
              <div className="flex-1 p-6 overflow-auto">
                <div className="w-full relative bg-black rounded-lg overflow-hidden" style={{ minHeight: '500px', aspectRatio: '16/9' }}>
                  <VideoPlayer
                    src={videoUrl}
                    title={`Analysis: ${testName || 'Test'}`}
                    enableControls={true}
                    className="w-full h-full"
                  />
                </div>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default AnalysisVideoModal;

